import { claimChartDraft, listChartDrafts, readChartDraft, saveChartDraft, updateChartDraft } from './chartDraftStore';
import { removeRequest } from './offlineQueue';
import { createReplayContext, isBillingSession } from './replayContext';
import { isClinicalChartWrite } from './config';

let running = false;

function classify(response) {
  if (!response) return 'transient';
  if (response.status === 401) return 'authentication';
  if (response.status === 403) return 'authorization';
  if ([408, 425, 429].includes(response.status) || response.status >= 500) return 'transient';
  if (response.status === 409 || response.status === 412) return 'conflict';
  if (response.status >= 400 && response.status < 500) return 'validation';
  return 'transient';
}

export async function syncChartDrafts({ retryActionRequired = false, draftId = null } = {}) {
  if (running || !navigator.onLine || !localStorage.getItem('velaToken') || isBillingSession()) return;
  const replay = createReplayContext();
  running = true;
  try {
    const drafts = (await listChartDrafts()).sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    for (const summary of drafts) {
      if (!replay.canContinue()) return;
      if (draftId && summary.draftId !== draftId) continue;
      if (summary.recoveryOnly || !['saved-on-device', 'syncing', 'action-required'].includes(summary.status)
        || (summary.status === 'action-required' && !retryActionRequired)
        || summary.errorCategory === 'authorization') continue;
      if (Date.parse(summary.nextAttemptAt) > Date.now()) continue;
      const draft = await claimChartDraft(summary.draftId, { retryActionRequired });
      if (!draft) continue;
      if (draft.recoveryOnly || !isClinicalChartWrite(new URL(draft.request.url, window.location.origin).pathname, draft.request.method)) continue;
      if (!replay.canContinue()) return;
      await updateChartDraft(draft.draftId, { status: 'syncing', errorCategory: null, errorMessage: null });
      try {
        if (!replay.canContinue()) return;
        const headers = new Headers(draft.request.headers || {});
        headers.set('Authorization', `Bearer ${replay.token}`);
        headers.set('X-Sena-Offline-Draft-Id', draft.draftId);
        headers.set('Idempotency-Key', draft.draftId);
        headers.set('X-Sena-Offline-Replay', 'true');
        if (draft.baseVersion && ['PUT', 'PATCH'].includes(draft.request.method)) {
          headers.set('X-Sena-Base-Version', draft.baseVersion);
        }
        const response = await fetch(draft.request.url, {
          method: draft.request.method,
          headers,
          body: draft.request.body,
          signal: replay.signal,
        });
        if (!replay.canContinue()) return;
        if (response.ok) {
          const serverData = response.headers.get('content-type')?.includes('application/json')
            ? await response.clone().json().catch(() => null)
            : null;
          const serverChartId = serverData?.id ?? serverData?.Id ?? draft.chartId ?? null;
          await updateChartDraft(draft.draftId, {
            status: 'synced',
            syncedAt: new Date().toISOString(),
            serverChartId,
            chartId: serverChartId,
            errorCategory: null,
            errorMessage: null,
          });
          if (draft.legacyRequestId) {
            try {
              await removeRequest(draft.legacyRequestId);
              window.dispatchEvent(new CustomEvent('vela:legacy-queue-changed', {
                detail: { requestId: draft.legacyRequestId, status: 'recovered' },
              }));
            } catch (error) {
              console.error('Chart synced, but its legacy queue record could not be removed:', error);
            }
          }
          window.dispatchEvent(new CustomEvent('vela:chart-synced', {
            detail: {
              draftId: draft.draftId,
              chartType: draft.chartType,
              clientReferenceId: draft.clientReferenceId,
              pregnancyReferenceId: draft.pregnancyReferenceId,
              pregnancyId: draft.pregnancyId,
              serverData,
            },
          }));
          continue;
        }
        const category = classify(response);
        const message = (await response.text()).slice(0, 500) || `Server returned ${response.status}`;
        let conflict = null;
        if (category === 'conflict' && draft.request.method === 'POST') {
          try { conflict = JSON.parse(message); } catch {}
        }
        await updateChartDraft(draft.draftId, {
          status: category === 'conflict' ? 'conflict' : category === 'transient' ? 'saved-on-device' : 'action-required',
          attemptCount: (draft.attemptCount || 0) + 1,
          nextAttemptAt: category === 'transient' ? new Date(Date.now() + Math.min(300000, 1000 * (2 ** ((draft.attemptCount || 0) + 1)))).toISOString() : null,
          errorCategory: category,
          errorMessage: message,
          requestMethod: draft.request.method,
          conflictingVisitId: conflict?.conflictingVisitId || null,
          conflictingVisitReferenceId: conflict?.conflictingVisitReferenceId || null,
        });
      } catch (error) {
        if (!replay.canContinue()) return;
        const attemptCount = (draft.attemptCount || 0) + 1;
        await updateChartDraft(draft.draftId, {
          status: 'saved-on-device',
          attemptCount,
          nextAttemptAt: new Date(Date.now() + Math.min(300000, 1000 * (2 ** attemptCount))).toISOString(),
          errorCategory: 'transient',
          errorMessage: 'Connection interrupted; the draft remains on this device.',
        });
      }
    }
  } finally {
    replay.dispose();
    running = false;
  }
}

export async function syncReviewedConflict(draftId) {
  if (isBillingSession()) throw new Error('Clinical recovery is not available in the billing workspace.');
  const replay = createReplayContext();
  try {
    const draft = await readChartDraft(draftId);
    if (!replay.canContinue()) throw new Error('Practice access changed. Reopen the clinical workspace before continuing.');
    if (!draft || draft.recoveryOnly || draft.status !== 'conflict' || !draft.reviewedAt) {
      throw new Error('Review this device draft before resolving the conflict.');
    }
    if (!['PUT', 'PATCH'].includes(draft.request.method)) {
      throw new Error('Only chart updates can be resolved with this recovery action.');
    }
    if (!navigator.onLine || !localStorage.getItem('velaToken')) {
      throw new Error('Reconnect and sign in before resolving this conflict.');
    }

    const response = await fetch(draft.request.url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${replay.token}` },
      signal: replay.signal,
    });
    if (!replay.canContinue()) throw new Error('Practice access changed. Reopen the clinical workspace before continuing.');
    if (!response.ok) {
      throw new Error(`The current server chart could not be loaded (${response.status}).`);
    }
    const serverData = await response.json();
    if (!replay.canContinue()) throw new Error('Practice access changed. Reopen the clinical workspace before continuing.');
    const baseVersion = serverData?.updatedAt || serverData?.UpdatedAt
      || serverData?.createdAt || serverData?.CreatedAt;
    if (!baseVersion) {
      throw new Error('The server chart did not include a version. The device draft remains unchanged.');
    }

    const replacement = await saveChartDraft({
      ...draft,
      ...draft.request,
      draftId: null,
      coalesce: false,
      reviewedReplacementOf: draftId,
      baseVersion,
      status: 'saved-on-device',
      errorCategory: null,
      errorMessage: null,
    });
    await updateChartDraft(draftId, { status: 'legacy-review', supersededBy: replacement.draftId });
    await syncChartDrafts({ draftId: replacement.draftId });
  } finally {
    replay.dispose();
  }
}

export function installChartSyncTriggers() {
  const sync = () => syncChartDrafts();
  window.addEventListener('online', sync);
  window.addEventListener('vela:logged-in', sync);
  window.addEventListener('focus', sync);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) sync(); });
  sync();
  // A radio interruption may never fire an online event. The per-draft due
  // time above prevents focus and this bounded retry tick from hot-looping.
  const timer = window.setInterval(() => { if (!document.hidden) sync(); }, 30000);
  return () => window.clearInterval(timer);
}
